import { DataLoader } from './data-loader.js';
import { PeopleQuizGenerator, PeopleQuizQuestion } from './team-quiz-people-generator.js';

type QuizState = 'welcome' | 'question' | 'reveal' | 'results';

class GitHubWrappedPeopleQuiz {
  private dataLoader: DataLoader;
  private quizGenerator?: PeopleQuizGenerator;
  private questions: PeopleQuizQuestion[] = [];
  private currentQuestion = 0;
  private score = 0;
  private state: QuizState = 'welcome';
  private selectedOption: number | null = null;

  constructor() {
    this.dataLoader = new DataLoader();
  }

  async init(): Promise<void> {
    try {
      const data = await this.dataLoader.load();
      this.quizGenerator = new PeopleQuizGenerator(data);
      this.questions = this.quizGenerator.getQuestions();
      this.showWelcome();
    } catch (error) {
      this.showError('Erreur de chargement. Verifiez que data.json existe.');
    }
  }

  private showWelcome(): void {
    this.state = 'welcome';
    const container = document.getElementById('app')!;
    container.innerHTML = `
      <div class="cyber-orbs"></div>
      <a href="team.html" class="back-button">← Team Stats</a>
      <div class="quiz-welcome">
        <div class="quiz-emoji">🎯</div>
        <h1>Devine Qui!</h1>
        <p class="quiz-subtitle">Connaissez-vous vraiment votre equipe?</p>
        <div class="quiz-info">
          <div class="quiz-info-item">
            <span class="quiz-info-number">${this.questions.length}</span>
            <span class="quiz-info-label">Questions</span>
          </div>
          <div class="quiz-info-item">
            <span class="quiz-info-number">🏆</span>
            <span class="quiz-info-label">Stats Reveal</span>
          </div>
        </div>
        <button class="quiz-start-btn" id="startBtn">Commencer!</button>
        <p class="quiz-hint">Devinez quelle personne correspond a chaque stat!</p>
        <p class="quiz-hint" style="margin-top: 0.5rem; font-size: 0.8rem;">Apres chaque reponse, decouvrez les stats de toute l'equipe</p>
      </div>
    `;

    document.getElementById('startBtn')?.addEventListener('click', () => {
      this.startQuiz();
    });
  }

  private startQuiz(): void {
    this.currentQuestion = 0;
    this.score = 0;
    this.renderQuestion();
  }

  private renderQuestion(): void {
    this.state = 'question';
    const question = this.questions[this.currentQuestion];
    const container = document.getElementById('app')!;
    this.selectedOption = null;

    container.innerHTML = `
      <div class="cyber-orbs"></div>
      <div class="quiz-container">
        <div class="quiz-header">
          <div class="quiz-progress">
            <span class="quiz-current">${this.currentQuestion + 1}</span>
            <span class="quiz-separator">/</span>
            <span class="quiz-total">${this.questions.length}</span>
          </div>
          <div class="quiz-score">
            <span class="quiz-score-label">Score:</span>
            <span class="quiz-score-value">${this.score}</span>
          </div>
        </div>
        
        <div class="quiz-progress-bar">
          <div class="quiz-progress-fill" style="width: ${((this.currentQuestion) / this.questions.length) * 100}%"></div>
        </div>

        <div class="quiz-question-card">
          <div class="quiz-question-emoji">${question.emoji}</div>
          <div class="quiz-fun-title">${question.funTitle}</div>
          <h2 class="quiz-question-text">${question.question}</h2>
          
          <div class="quiz-people-options">
            ${question.options.map((person, index) => `
              <button class="quiz-person-option" data-index="${index}">
                <span class="quiz-person-avatar">${this.getInitials(person)}</span>
                <span class="quiz-person-name">${person}</span>
              </button>
            `).join('')}
          </div>
        </div>
      </div>
    `;

    // Add click handlers for options
    document.querySelectorAll('.quiz-person-option').forEach(option => {
      option.addEventListener('click', (e) => {
        const index = parseInt((e.currentTarget as HTMLElement).dataset.index!);
        this.selectOption(index);
      });
    });
  }

  private getInitials(name: string): string {
    return name
      .split(' ')
      .map(part => part.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);
  }

  private selectOption(index: number): void {
    if (this.selectedOption !== null) return;
    
    this.selectedOption = index;
    const question = this.questions[this.currentQuestion];
    const isCorrect = index === question.correctIndex;

    if (isCorrect) {
      this.score++;
    }

    // Update UI - show correct/wrong
    document.querySelectorAll('.quiz-person-option').forEach((option, i) => {
      const optionEl = option as HTMLElement;
      optionEl.classList.add('disabled');
      
      if (i === question.correctIndex) {
        optionEl.classList.add('correct');
      } else if (i === index && !isCorrect) {
        optionEl.classList.add('wrong');
      }
    });

    // Update score display
    document.querySelector('.quiz-score-value')!.textContent = this.score.toString();

    // Show feedback briefly, then transition to stats reveal
    const questionCard = document.querySelector('.quiz-question-card')!;
    const feedbackHtml = `
      <div class="quiz-answer-feedback ${isCorrect ? 'correct' : 'wrong'}">
        <span class="feedback-icon">${isCorrect ? '🎉' : '😅'}</span>
        <span class="feedback-text">${isCorrect ? 'Bien joue!' : 'Pas tout a fait...'}</span>
      </div>
    `;
    questionCard.insertAdjacentHTML('beforeend', feedbackHtml);

    // Transition to stats reveal after a short delay
    setTimeout(() => {
      this.showStatsReveal();
    }, 1500);
  }

  private showStatsReveal(): void {
    this.state = 'reveal';
    const question = this.questions[this.currentQuestion];
    const reveal = question.statsReveal;
    const container = document.getElementById('app')!;

    container.innerHTML = `
      <div class="cyber-orbs"></div>
      <div class="stats-reveal-container">
        <div class="stats-reveal-header">
          <div class="stats-reveal-emoji">${reveal.emoji}</div>
          <h2 class="stats-reveal-title">${reveal.title}</h2>
          <p class="stats-reveal-subtitle">${reveal.subtitle}</p>
        </div>

        <div class="stats-reveal-main">
          <div class="main-stat-card">
            <div class="main-stat-label">${reveal.mainStat.label}</div>
            <div class="main-stat-value">${reveal.mainStat.value}</div>
          </div>
        </div>

        ${reveal.example ? `
          <div class="stats-reveal-example">
            <div class="example-icon">${reveal.example.icon || '📌'}</div>
            <div class="example-content">
              <div class="example-label">${reveal.example.label}</div>
              <div class="example-text">${reveal.example.content}</div>
            </div>
          </div>
        ` : ''}

        ${reveal.topContributors && reveal.topContributors.length > 0 ? `
          <div class="stats-reveal-comparison">
            <div class="comparison-title">Top Contributeurs</div>
            <div class="comparison-list">
              ${reveal.topContributors.slice(0, 5).map((item, index) => `
                <div class="comparison-item ${item.isWinner ? 'winner' : ''}" style="animation-delay: ${index * 0.1}s">
                  <div class="comparison-rank">
                    ${index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                  </div>
                  <div class="comparison-name">${item.name}</div>
                  <div class="comparison-value">${item.value}</div>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        ${reveal.funFact ? `
          <div class="stats-reveal-funfact">
            <span class="funfact-icon">💡</span>
            <span class="funfact-text">${reveal.funFact}</span>
          </div>
        ` : ''}

        <button class="quiz-next-btn" id="nextBtn">
          ${this.currentQuestion < this.questions.length - 1 ? 'Question Suivante →' : 'Voir les Resultats →'}
        </button>
      </div>
    `;

    document.getElementById('nextBtn')?.addEventListener('click', () => {
      this.nextQuestion();
    });

    // Keyboard shortcut
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
        document.removeEventListener('keydown', handleKeyPress);
        this.nextQuestion();
      }
    };
    document.addEventListener('keydown', handleKeyPress);
  }

  private nextQuestion(): void {
    if (this.currentQuestion < this.questions.length - 1) {
      this.currentQuestion++;
      this.renderQuestion();
    } else {
      this.showResults();
    }
  }

  private showResults(): void {
    this.state = 'results';
    const container = document.getElementById('app')!;
    const percentage = Math.round((this.score / this.questions.length) * 100);
    
    let grade = '';
    let gradeEmoji = '';
    let message = '';

    if (percentage >= 90) {
      grade = 'Expert Equipe';
      gradeEmoji = '🏆';
      message = 'Vous connaissez votre equipe par coeur!';
    } else if (percentage >= 70) {
      grade = 'Tres Bien';
      gradeEmoji = '🌟';
      message = 'Excellente connaissance de l\'equipe!';
    } else if (percentage >= 50) {
      grade = 'Bien';
      gradeEmoji = '👍';
      message = 'Pas mal! Vous suivez bien.';
    } else if (percentage >= 30) {
      grade = 'En Progres';
      gradeEmoji = '🤔';
      message = 'Il est temps de mieux connaitre vos collegues!';
    } else {
      grade = 'Debutant';
      gradeEmoji = '😅';
      message = 'Plus de pauses cafe avec l\'equipe!';
    }

    container.innerHTML = `
      <div class="cyber-orbs"></div>
      <div class="quiz-results">
        <div class="quiz-results-emoji">${gradeEmoji}</div>
        <h1 class="quiz-results-title">Quiz Termine!</h1>
        
        <div class="quiz-results-score">
          <div class="quiz-results-score-circle">
            <span class="quiz-results-score-number">${this.score}</span>
            <span class="quiz-results-score-total">/ ${this.questions.length}</span>
          </div>
          <div class="quiz-results-percentage">${percentage}%</div>
        </div>

        <div class="quiz-results-grade">
          <span class="quiz-grade-label">Grade:</span>
          <span class="quiz-grade-value">${grade}</span>
        </div>

        <p class="quiz-results-message">${message}</p>

        <div class="quiz-results-actions">
          <button class="quiz-retry-btn" id="retryBtn">Rejouer</button>
          <a href="team-quiz.html" class="quiz-other-link">Autre Quiz</a>
          <a href="team.html" class="quiz-team-link">Voir les Stats</a>
        </div>
      </div>
    `;

    document.getElementById('retryBtn')?.addEventListener('click', () => {
      // Re-shuffle questions
      this.questions = this.quizGenerator!.getQuestions();
      this.startQuiz();
    });
  }

  private showError(message: string): void {
    const container = document.getElementById('app')!;
    container.innerHTML = `
      <div class="error">
        <h1>Erreur</h1>
        <p>${message}</p>
        <a href="team.html" class="quiz-team-link" style="margin-top: 2rem;">Retour aux Stats</a>
      </div>
    `;
  }
}

// Initialize the quiz
const quiz = new GitHubWrappedPeopleQuiz();
quiz.init();
